import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import { toCreasedNormals } from "three-stdlib";

const SELECTION_OUTLINE_COLOR = "#2563eb";
const SELECTION_OUTLINE_RENDER_ORDER = 10000;
const SELECTION_OUTLINE_STENCIL_REF = 7;

type SelectableMesh = THREE.Mesh | THREE.SkinnedMesh | THREE.InstancedMesh;

type SelectedMeshOutlineProps = {
  visible?: boolean;
  color?: THREE.ColorRepresentation;
  thickness?: number;
  opacity?: number;
  angle?: number;
  /**
   * Outline hull side. Closed/volumetric meshes use BackSide (default). Planar
   * FrontSide wall faces must use FrontSide or DoubleSide or the rim is culled.
   */
  side?: THREE.Side;
};

const vertexShader = `
  #include <common>
  #include <morphtarget_pars_vertex>
  #include <skinning_pars_vertex>
  #include <clipping_planes_pars_vertex>

  uniform float thickness;
  uniform vec2 size;

  void main() {
    #if defined (USE_SKINNING)
      #include <beginnormal_vertex>
      #include <morphnormal_vertex>
      #include <skinbase_vertex>
      #include <skinnormal_vertex>
      #include <defaultnormal_vertex>
    #endif

    #include <begin_vertex>
    #include <morphtarget_vertex>
    #include <skinning_vertex>
    #include <project_vertex>
    #include <clipping_planes_vertex>

    vec4 outlineNormal = vec4(normal, 0.0);
    vec4 outlinePosition = vec4(transformed, 1.0);

    #ifdef USE_INSTANCING
      outlineNormal = instanceMatrix * outlineNormal;
      outlinePosition = instanceMatrix * outlinePosition;
    #endif

    vec4 clipPosition = projectionMatrix * modelViewMatrix * outlinePosition;
    vec4 clipNormal = projectionMatrix * modelViewMatrix * outlineNormal;
    vec2 offset = normalize(clipNormal.xy) * thickness / size * clipPosition.w * 2.0;

    clipPosition.xy += offset;
    gl_Position = clipPosition;
  }
`;

const fragmentShader = `
  uniform vec3 color;
  uniform float opacity;

  #include <clipping_planes_pars_fragment>

  void main() {
    #include <clipping_planes_fragment>
    gl_FragColor = vec4(color, opacity);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

const SelectedMeshOutline: React.FC<SelectedMeshOutlineProps> = ({
  visible = false,
  color = SELECTION_OUTLINE_COLOR,
  thickness = 4,
  opacity = 1,
  angle = 0,
  side = THREE.BackSide,
}) => {
  const groupRef = useRef<THREE.Group | null>(null);
  const outlineMeshRef = useRef<SelectableMesh | null>(null);
  const maskMeshRef = useRef<SelectableMesh | null>(null);
  const sourceMeshRef = useRef<SelectableMesh | null>(null);
  const sourceGeometryRef = useRef<THREE.BufferGeometry | null>(null);
  const angleRef = useRef<number | null>(null);
  const { gl, size } = useThree();

  const maskMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        colorWrite: false,
        depthTest: false,
        depthWrite: false,
        side: THREE.BackSide,
        stencilWrite: true,
        stencilRef: SELECTION_OUTLINE_STENCIL_REF,
        stencilFunc: THREE.AlwaysStencilFunc,
        stencilFail: THREE.ReplaceStencilOp,
        stencilZFail: THREE.ReplaceStencilOp,
        stencilZPass: THREE.ReplaceStencilOp,
      }),
    [],
  );

  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          color: { value: new THREE.Color(color) },
          opacity: { value: opacity },
          thickness: { value: thickness },
          size: { value: gl.getDrawingBufferSize(new THREE.Vector2()) },
        },
        vertexShader,
        fragmentShader,
        side: THREE.BackSide,
        transparent: true,
        toneMapped: false,
        depthTest: false,
        depthWrite: false,
        stencilWrite: true,
        stencilRef: SELECTION_OUTLINE_STENCIL_REF,
        stencilFunc: THREE.NotEqualStencilFunc,
        stencilFail: THREE.KeepStencilOp,
        stencilZFail: THREE.KeepStencilOp,
        stencilZPass: THREE.KeepStencilOp,
      }),
    [gl],
  );

  useLayoutEffect(() => {
    const group = groupRef.current;
    const sourceMesh = group?.parent;
    if (!group || !isSelectableMesh(sourceMesh)) return;
    if (!visible) {
      removeOutlineMesh(group, outlineMeshRef.current, angleRef.current);
      removeOutlineMesh(group, maskMeshRef.current, null);
      outlineMeshRef.current = null;
      maskMeshRef.current = null;
      sourceMeshRef.current = null;
      sourceGeometryRef.current = null;
      angleRef.current = null;
      return;
    }

    if (
      sourceMeshRef.current === sourceMesh &&
      sourceGeometryRef.current === sourceMesh.geometry &&
      angleRef.current === angle
    ) {
      return;
    }

    removeOutlineMesh(group, outlineMeshRef.current, angleRef.current);
    removeOutlineMesh(group, maskMeshRef.current, null);

    const maskMesh = createOutlineMesh(sourceMesh, maskMaterial);
    maskMesh.geometry = sourceMesh.geometry;
    maskMesh.renderOrder = SELECTION_OUTLINE_RENDER_ORDER - 1;
    maskMesh.frustumCulled = false;
    maskMesh.raycast = () => null;
    maskMesh.morphTargetInfluences = sourceMesh.morphTargetInfluences;
    maskMesh.morphTargetDictionary = sourceMesh.morphTargetDictionary;

    const outlineMesh = createOutlineMesh(sourceMesh, material);
    outlineMesh.geometry = angle
      ? toCreasedNormals(sourceMesh.geometry, angle)
      : sourceMesh.geometry;
    outlineMesh.renderOrder = SELECTION_OUTLINE_RENDER_ORDER;
    outlineMesh.frustumCulled = false;
    outlineMesh.raycast = () => null;
    outlineMesh.morphTargetInfluences = sourceMesh.morphTargetInfluences;
    outlineMesh.morphTargetDictionary = sourceMesh.morphTargetDictionary;

    group.add(maskMesh);
    group.add(outlineMesh);
    maskMeshRef.current = maskMesh;
    outlineMeshRef.current = outlineMesh;
    sourceMeshRef.current = sourceMesh;
    sourceGeometryRef.current = sourceMesh.geometry;
    angleRef.current = angle;
  });

  useLayoutEffect(() => {
    const size = gl.getDrawingBufferSize(new THREE.Vector2());
    maskMaterial.visible = visible;
    maskMaterial.side = side;
    maskMaterial.needsUpdate = true;
    material.uniforms.color.value.set(color);
    material.uniforms.opacity.value = opacity;
    material.uniforms.thickness.value = thickness;
    material.uniforms.size.value.copy(size);
    material.side = side;
    material.visible = visible;
    material.needsUpdate = true;
  }, [
    color,
    gl,
    material,
    maskMaterial,
    opacity,
    side,
    size.height,
    size.width,
    thickness,
    visible,
  ]);

  useEffect(
    () => () => {
      const group = groupRef.current;
      if (group) {
        removeOutlineMesh(group, outlineMeshRef.current, angleRef.current);
        removeOutlineMesh(group, maskMeshRef.current, null);
      }
      material.dispose();
      maskMaterial.dispose();
    },
    [maskMaterial, material],
  );

  return (
    <group
      ref={groupRef}
      visible={visible}
      // Export snapshots clone the live scene; keep selection UI out of those renders.
      userData={{ excludeFromSceneClone: true }}
    />
  );
};

function isSelectableMesh(
  object: THREE.Object3D | null | undefined,
): object is SelectableMesh {
  return Boolean(
    object && (object as THREE.Mesh).isMesh && (object as THREE.Mesh).geometry,
  );
}

function createOutlineMesh(
  sourceMesh: SelectableMesh,
  material: THREE.Material,
): SelectableMesh {
  if ((sourceMesh as THREE.SkinnedMesh).isSkinnedMesh) {
    const mesh = new THREE.SkinnedMesh(sourceMesh.geometry, material);
    mesh.bind(
      (sourceMesh as THREE.SkinnedMesh).skeleton,
      (sourceMesh as THREE.SkinnedMesh).bindMatrix,
    );
    return mesh;
  }

  if ((sourceMesh as THREE.InstancedMesh).isInstancedMesh) {
    const sourceInstancedMesh = sourceMesh as THREE.InstancedMesh;
    const mesh = new THREE.InstancedMesh(
      sourceInstancedMesh.geometry,
      material,
      sourceInstancedMesh.count,
    );
    mesh.instanceMatrix = sourceInstancedMesh.instanceMatrix;
    return mesh;
  }

  return new THREE.Mesh(sourceMesh.geometry, material);
}

function removeOutlineMesh(
  group: THREE.Group,
  outlineMesh: SelectableMesh | null,
  angle: number | null,
): void {
  if (!outlineMesh) return;
  if (angle) outlineMesh.geometry.dispose();
  group.remove(outlineMesh);
}

export default SelectedMeshOutline;
